import { Add, ContentPaste } from '@mui/icons-material';
import {
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Tooltip,
} from '@mui/material';
import { Box, SxProps, Theme } from '@mui/system';
import { FieldType } from 'api/models/FieldTemplate';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MaterialIcons from '@mui/icons-material';
import MuiIcon from 'components/MuiIcon/MuiIcon';
import { useRecoilValue } from 'recoil';
import { clipboardAtom } from 'state/clipboardState';
import useAddFieldTemplate from 'hooks/useAddFieldTemplate';
import usePasteField from 'hooks/usePasteFieldTemplate';

const LIST_DIVIDER = 'LIST_DIVIDER';

const FIELD_TYPES = [
  'number',
  'text',
  'select',
  'date',
  // 'combined',
  'calculated',
  // 'file',
  LIST_DIVIDER,
  'layout',
  'selectable-table',
  'divider',
  'header',
] as const;

export function getFieldIcon(fieldType?: string): keyof typeof MaterialIcons {
  switch (fieldType) {
    case 'number':
      return 'FormatListNumbered';
    case 'text':
      return 'TextFields';
    case 'select':
      return 'ArrowDropDown';
    case 'selectable-table':
      return 'TableChart';
    case 'date':
      return 'DateRange';
    case 'combined':
      return 'MergeType';
    case 'calculated':
      return 'Functions';
    case 'file':
      return 'AttachFile';
    case 'layout':
      return 'ViewModule';
    case 'divider':
    case 'horizontal-separator':
      return 'Remove';
    case 'header':
      return 'Title';

    default:
      return 'ViewModule';
  }
}

interface AddFieldButtonProps {
  sx?: SxProps<Theme>;
  showLabel?: boolean;
  insertIndex: number;
  parentPath: string;
  disabled?: boolean;
}

export default function AddFieldButton({
  sx,
  showLabel = true,
  insertIndex = 0,
  parentPath,
  disabled,
}: AddFieldButtonProps) {
  const { t } = useTranslation();
  const clipboard = useRecoilValue(clipboardAtom);

  const add = useAddFieldTemplate({
    path: parentPath,
    insertIndex,
  });

  const paste = usePasteField({
    path: parentPath,
    insertIndex,
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box width={'100%'} display={'flex'} justifyContent={'end'} sx={sx}>
      <Button
        disabled={disabled}
        variant="text"
        onClick={handleClick}
        size="small"
      >
        <Add /> {showLabel && t('forms.addField')}
      </Button>
      <Menu
        id="add-form-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'add-form-menu-button',
        }}
        sx={{ ml: -6 }}
      >
        <MenuList>
          {FIELD_TYPES.map(fieldType => {
            if (fieldType === LIST_DIVIDER) {
              return <Divider key={fieldType} />;
            }
            return (
              <MenuItem
                key={fieldType}
                onClick={() => {
                  add({
                    type: fieldType as FieldType,
                  });
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <MuiIcon icon={getFieldIcon(fieldType)} sx={{ mr: 1 }} />
                </ListItemIcon>
                <ListItemText>{t(`forms.fieldType.${fieldType}`)}</ListItemText>
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
      {clipboard && !disabled && (
        <Box
          sx={{
            background: theme => theme.palette.primary.main,
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            ml: 1,
          }}
        >
          <Tooltip title={t('general.paste')}>
            <span>
              <IconButton size="small" onClick={paste} disabled={disabled}>
                <ContentPaste
                  fontSize="small"
                  sx={{
                    color: theme =>
                      theme.palette.getContrastText(theme.palette.primary.main),
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
